import React from 'react';
import { 
  Box, 
  Typography, 
  MenuItem, 
  SelectChangeEvent,
  Paper,
  Button,
  Select
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { 
  fetchSubscriptionPlans, 
  selectSubscriptionPlans 
} from '../../../../modules/subscription';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { styled, alpha } from '@mui/material/styles';
import apiCall from '../../../../utils/api';
import AlertPopup from '../../../../components/common/AlertPopup';
import { CircularProgress } from '@mui/material';

const primaryColor = '#5E8E7E';

const PageWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#F8F9FA',
  minHeight: '100vh',
}));

const UpgradeContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '12px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  backgroundColor: '#fff',
}));

const FormSectionHeader = styled(Box)(({ theme }) => ({
  backgroundColor: primaryColor,
  color: '#fff',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  fontSize: '15px',
  marginBottom: theme.spacing(3),
  borderRadius: '4px',
}));

const FormGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(0, 3),
}));

const Label = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
  marginBottom: '8px',
});

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  padding: theme.spacing(0, 3),
}));

const CancelButton = styled(Button)({
  textTransform: 'none',
  padding: '10px 40px',
  borderRadius: '8px',
  border: `1px solid ${primaryColor}`,
  color: primaryColor,
  fontWeight: 600,
  fontSize: '15px',
  minWidth: '150px',
  '&:hover': {
    backgroundColor: alpha(primaryColor, 0.05),
    borderColor: primaryColor,
  }
});

const UpgradeButton = styled(Button)({
  textTransform: 'none',
  padding: '10px 40px',
  borderRadius: '8px',
  backgroundColor: primaryColor,
  color: '#fff',
  fontWeight: 600,
  fontSize: '15px',
  minWidth: '180px',
  '&:hover': {
    backgroundColor: alpha(primaryColor, 0.9),
  },
  '&.Mui-disabled': {
    backgroundColor: '#E0E0E0',
  }
});

const SubscriptionUpgradePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const plans = useSelector(selectSubscriptionPlans);
  const [selectedPlan, setSelectedPlan] = React.useState('');
  const [startDate, setStartDate] = React.useState<Dayjs | null>(dayjs());
  const [expiryDate, setExpiryDate] = React.useState<Dayjs | null>(dayjs().add(29, 'day'));
  const [isStartDateChanged, setIsStartDateChanged] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [alert, setAlert] = React.useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  const currentPlan = plans.find(p => p.plan_id.toString() === selectedPlan);
  const maxExpiryDate = (currentPlan && startDate) ? startDate.add(currentPlan.plan_expiry_in_days - 1, 'day') : undefined;

  const fetchOrgDetails = React.useCallback(async () => {
    try {
      const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${id}/`);
      if (response.data && response.data.data) {
        const subData = response.data.data.plan_details;
        if (subData) {
          setSelectedPlan(subData.plan_id?.toString() || '');
          if (subData.start_date) {
            const sDate = dayjs(subData.start_date);
            setStartDate(sDate);
          }
          if (subData.end_date) setExpiryDate(dayjs(subData.end_date));
        }
      }
    } catch (error) {
      console.error('Error fetching org details:', error);
    }
  }, [id]);

  React.useEffect(() => {
    setActivePage('organizations');
    dispatch(fetchSubscriptionPlans({ page: 1, pageSize: 100, status: 'Active' }));
    fetchOrgDetails();
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Organizations', path: '/super-admin/organizations' },
      { title: 'Overview', path: `/super-admin/organizations/${id}` },
      { title: 'Subscription Plan', path: `/super-admin/organizations/${id}/subscription` },
      { title: 'Upgrade Plan', path: `/super-admin/organizations/${id}/subscription/upgrade` },
    ]);
    setIsShow(true);
  }, [dispatch, id, setActivePage, setPath, setIsShow, fetchOrgDetails]);

  const handlePlanChange = (event: SelectChangeEvent<unknown>) => {
    const planId = event.target.value as string;
    setSelectedPlan(planId);
    
    // Auto-calculate expiry if a new plan is selected and we have a start date
    const plan = plans.find(p => p.plan_id.toString() === planId);
    if (plan && startDate) {
      setExpiryDate(startDate.add(plan.plan_expiry_in_days - 1, 'day'));
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    if (currentPlan && startDate && expiryDate) {
      const maxAllowed = startDate.add(currentPlan.plan_expiry_in_days - 1, 'day');
      if (expiryDate.isAfter(maxAllowed)) {
        setAlert({ 
          message: `Expiry date cannot exceed the plan validity of ${currentPlan.plan_expiry_in_days} days (Max Allowed: ${maxAllowed.format('DD-MM-YYYY')})`, 
          severity: 'error' 
        });
        return;
      }
    }
    
    setProcessing(true);
    try {
      const response = await apiCall('post', '/api/subscriptions/org-subscriptions/', {
        organization_id: id,
        plan_id: selectedPlan,
        start_date: startDate?.toISOString(),
        end_date: expiryDate?.toISOString()
      });
      
      if (response.status === 200 || response.status === 201) {
        setAlert({ message: 'Subscription assigned successfully!', severity: 'success' });
        setTimeout(() => {
          navigate(`/super-admin/organizations/${id}/subscription`);
        }, 1500);
      }
    } catch (error: any) {
      setAlert({ message: error.response?.data?.message || 'Failed to assign subscription', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PageWrapper>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>Manage Subscriptions</Typography>
      </Box>

      <UpgradeContainer elevation={0}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 4, px: 3 }}>
          Assign Plan to Organization
        </Typography>

        {/* Section 1: Plan Selection */}
        <FormSectionHeader>Plan Selection</FormSectionHeader>
        <FormGroup sx={{ width: '50%' }}>
          <Label>Select Plan</Label>
          <Select
            value={selectedPlan}
            onChange={handlePlanChange}
            displayEmpty
            size="small"
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              width: '100%',
              height: '35px',
              borderRadius: '8px',
              backgroundColor: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E0E0E0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#B0B0B0',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: primaryColor,
              },
              '& .MuiSelect-select': {
                fontSize: '14px',
                color: selectedPlan ? '#333' : '#999',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '15px',
              },
            }}
          >
            <MenuItem value="" disabled>Choose a subscription plan</MenuItem>
            {plans.filter(p => p.is_active).map((plan) => (
              <MenuItem key={plan.plan_id} value={plan.plan_id.toString()}>
                {plan.plan_name} (${plan.plan_price_month}/mo)
              </MenuItem>
            ))}
          </Select>
        </FormGroup>

        {/* Section 2: Assignment Settings */}
        <FormSectionHeader>Assignment Settings</FormSectionHeader>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 4, mb: 3, px: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Label>Start Date</Label>
              <DatePicker
                format="DD-MM-YYYY"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setIsStartDateChanged(true);
                  if (newValue && expiryDate && (newValue.isAfter(expiryDate) || newValue.isSame(expiryDate))) {
                    const days = currentPlan ? currentPlan.plan_expiry_in_days - 1 : 29;
                    setExpiryDate(newValue.add(days, 'day'));
                  }
                }}
                disablePast={isStartDateChanged}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                      }
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Label>Expiry Date</Label>
              <DatePicker
                format="DD-MM-YYYY"
                value={expiryDate}
                onChange={(newValue) => setExpiryDate(newValue)}
                disablePast
                minDate={startDate ? startDate.add(1, 'day') : dayjs().add(1, 'day')}
                maxDate={maxExpiryDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>

        <ActionButtons>
          <CancelButton onClick={() => navigate(-1)}>
            Cancel
          </CancelButton>
          <UpgradeButton 
            variant="contained" 
            disableElevation
            disabled={!selectedPlan || processing}
            onClick={handleUpgrade}
          >
            {processing ? <CircularProgress size={20} color="inherit" /> : 'Assign Subscription'}
          </UpgradeButton>
        </ActionButtons>
      </UpgradeContainer>

      <AlertPopup 
        message={alert?.message || ''} 
        severity={alert?.severity || 'success'} 
        open={!!alert} 
        onClose={() => setAlert(null)} 
      />
    </PageWrapper>
  );
};

export default SubscriptionUpgradePage;
